import type { IconName } from '@/utils/iconName';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

export interface SidebarLink {
  label: string;
  href: string;
  iconName?: IconName;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  isExternal?: boolean;
  disabled?: boolean;
  /**
   * Will force the active state
   */
  isActive?: boolean;
}

export interface SidebarLinkGroup {
  links: SidebarLink[];
  label?: string;
  disabled?: boolean;
  /**
   * Default: path
   */
  isActiveMatchingMethod?: 'path' | 'searchParam';
}

export type BackMode = 'history' | 'route' | 'custom';

export interface BackConfig {
  label?: string;
  to?: string;
  onClick?: () => void;
  mode?: BackMode;
}

export interface ContextSidebarConfig {
  groups: SidebarLinkGroup[];
  back?: BackConfig;
  /**
   * Optional node rendered at the top of the sidebar body, above the groups
   * (e.g. an event switcher). Its identity is intentionally NOT compared in
   * shallowEqualConfig (a fresh node every render would loop). Provide
   * `headerKey` to signal when the header's content has meaningfully changed.
   */
  header?: React.ReactNode;
  /**
   * Stable key describing the current `header` content. Participates in
   * equality so the header re-renders only when this changes.
   */
  headerKey?: string;
  /**
   * 'collapsible' - groups render as collapsible sections (like AppSidebar)
   * 'grouped' - groups render as flat lists with labels
   * @default 'collapsible'
   */
  mode?: 'collapsible' | 'grouped';
}

interface SidebarLayer {
  id: string;
  config: ContextSidebarConfig | null;
}

interface SidebarState {
  layers: SidebarLayer[];
}

type SidebarAction =
  | { type: 'REGISTER_LAYER'; payload: SidebarLayer }
  | { type: 'UPDATE_LAYER'; payload: SidebarLayer }
  | { type: 'UNREGISTER_LAYER'; payload: { id: string } };

const shallowEqualConfig = (
  a: ContextSidebarConfig | null,
  b: ContextSidebarConfig | null,
) => {
  if (a === b) return true;
  if (!a || !b) return false;

  if (a.mode !== b.mode) return false;

  // Compare the header by its key only; the node itself is rebuilt every
  // render so comparing identity would report a change on every pass.
  if (a.headerKey !== b.headerKey) return false;

  const aBack = a.back;
  const bBack = b.back;

  if (!!aBack !== !!bBack) return false;
  if (aBack && bBack) {
    if (aBack.label !== bBack.label) return false;
    if (aBack.to !== bBack.to) return false;
    if (aBack.mode !== bBack.mode) return false;
    // We intentionally ignore back.onClick identity to avoid infinite loops.
  }

  if (a.groups.length !== b.groups.length) return false;

  for (let i = 0; i < a.groups.length; i += 1) {
    const ag = a.groups[i];
    const bg = b.groups[i];

    if (!bg) return false;
    if (ag.label !== bg.label) return false;

    if (ag.links.length !== bg.links.length) return false;

    for (let j = 0; j < ag.links.length; j += 1) {
      const al = ag.links[j];
      const bl = bg.links[j];

      if (!bl) return false;
      if (al.label !== bl.label) return false;
      if (al.href !== bl.href) return false;
      if (al.isExternal !== bl.isExternal) return false;
      // We intentionally ignore icon and badge identity.
    }
  }

  return true;
};

const SidebarContext = React.createContext<{
  contextSidebar: ContextSidebarConfig | null;
  registerLayer: (id: string, config: ContextSidebarConfig | null) => void;
  updateLayer: (id: string, config: ContextSidebarConfig | null) => void;
  unregisterLayer: (id: string) => void;
} | null>(null);

const sidebarReducer = (
  state: SidebarState,
  action: SidebarAction,
): SidebarState => {
  switch (action.type) {
    case 'REGISTER_LAYER': {
      const existingIndex = state.layers.findIndex(
        (layer) => layer.id === action.payload.id,
      );

      if (existingIndex !== -1) {
        const nextLayers = [...state.layers];
        nextLayers[existingIndex] = action.payload;
        return { layers: nextLayers };
      }

      return {
        layers: [...state.layers, action.payload],
      };
    }
    case 'UPDATE_LAYER': {
      const existingIndex = state.layers.findIndex(
        (layer) => layer.id === action.payload.id,
      );

      if (existingIndex === -1) {
        return state;
      }

      const nextLayers = [...state.layers];
      nextLayers[existingIndex] = action.payload;
      return { layers: nextLayers };
    }
    case 'UNREGISTER_LAYER': {
      return {
        layers: state.layers.filter((layer) => layer.id !== action.payload.id),
      };
    }
    default:
      return state;
  }
};

export const SidebarContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [state, dispatch] = React.useReducer(sidebarReducer, { layers: [] });

  const registerLayer = React.useCallback(
    (id: string, config: ContextSidebarConfig | null) => {
      dispatch({ type: 'REGISTER_LAYER', payload: { id, config } });
    },
    [],
  );

  const updateLayer = React.useCallback(
    (id: string, config: ContextSidebarConfig | null) => {
      dispatch({ type: 'UPDATE_LAYER', payload: { id, config } });
    },
    [],
  );

  const unregisterLayer = React.useCallback((id: string) => {
    dispatch({ type: 'UNREGISTER_LAYER', payload: { id } });
  }, []);

  const contextSidebar = React.useMemo(() => {
    // Last registered, still-mounted layer with a non-null config wins
    for (let i = state.layers.length - 1; i >= 0; i -= 1) {
      const layer = state.layers[i];
      if (layer.config) {
        return layer.config;
      }
    }
    return null;
  }, [state.layers]);

  const value = React.useMemo(
    () => ({
      contextSidebar,
      registerLayer,
      updateLayer,
      unregisterLayer,
    }),
    [contextSidebar, registerLayer, updateLayer, unregisterLayer],
  );

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
};

export const useSidebarContext = () => {
  const ctx = React.useContext(SidebarContext);

  if (!ctx) {
    throw new Error(
      'useSidebarContext must be used within a SidebarContextProvider',
    );
  }

  return ctx;
};

interface UseRouteSidebarOptions {
  id?: string;
  priority?: number; // reserved for future use / metadata-based merging
}

export const useRouteSidebar = (
  config: ContextSidebarConfig | null,
  options?: UseRouteSidebarOptions,
) => {
  const { registerLayer, updateLayer, unregisterLayer } = useSidebarContext();
  const location = useLocation();

  const initialId = React.useMemo(
    () => options?.id ?? location.pathname,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const layerIdRef = React.useRef<string>(initialId);
  const lastConfigRef = React.useRef<ContextSidebarConfig | null>(
    config ?? null,
  );

  React.useEffect(() => {
    registerLayer(layerIdRef.current, config ?? null);

    return () => {
      unregisterLayer(layerIdRef.current);
    };
    // We only want to register/unregister on mount/unmount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const nextConfig = config ?? null;

    if (shallowEqualConfig(lastConfigRef.current, nextConfig)) {
      return;
    }

    lastConfigRef.current = nextConfig;
    updateLayer(layerIdRef.current, nextConfig);
  }, [config, updateLayer]);
};
